/**
 * CohortsPage — Blocks Phase 2 (§94). Harvest cohorts group blocks that ripen together
 * ("Early Kent 2026"); picking rounds are the staggered passes over a cohort as fruit
 * matures (mangoes get 2–3). Shows % picked + harvested weight per cohort.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Layers, Plus, X, ChevronDown, ChevronRight, Trash2 } from 'lucide-react'
import {
  cohortsApi, blocksApi, varietiesApi, prefsApi,
  type Cohort, type PickingRound, type HarvestWindow, type UserPreferences,
  type PestModel, type PestWatch,
} from '../lib/api'
import { weightUnit, inputToKg, kgToDisplayUnit, type MeasurementSystem } from '../lib/units'

const WINDOW_META: Record<HarvestWindow, { label: string; color: string }> = {
  early: { label: 'Early', color: '#84cc16' },
  mid:   { label: 'Mid',   color: '#F5A623' },
  late:  { label: 'Late',  color: '#dc2626' },
}
const ROUND_STATUS: Record<string, { label: string; color: string }> = {
  planned:     { label: 'Planned',     color: 'var(--color-text-muted)' },
  in_progress: { label: 'In progress', color: '#F5A623' },
  done:        { label: 'Done',        color: '#16a34a' },
}

const card: React.CSSProperties = { background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16 }
const lbl: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)' }

export function CohortsPage() {
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<Cohort | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  const { data: prefsData } = useQuery({ queryKey: ['user-preferences'], queryFn: (): Promise<UserPreferences> => prefsApi.get().then(r => r.data), staleTime: 300_000 })
  const sys: MeasurementSystem = prefsData?.measurement_system ?? 'imperial'
  const { data: cohorts } = useQuery({ queryKey: ['cohorts'], queryFn: () => cohortsApi.list().then(r => r.data.cohorts) })
  const fmtKg = (kg: number) => `${kgToDisplayUnit(kg, sys).toFixed(0)} ${weightUnit(sys)}`

  const open = (c?: Cohort) => { setEditing(c ?? null); setShowModal(true) }

  return (
    <main id="main-content" style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Layers size={20} /> Harvest cohorts
        </h1>
        <button onClick={() => open()} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600 }}>
          <Plus size={15} /> New cohort
        </button>
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 18 }}>
        Group the blocks that ripen together, then plan staggered picking rounds as the fruit matures.
      </p>

      {!cohorts?.length ? (
        <div style={{ ...card, textAlign: 'center', padding: '40px 24px' }}>
          <Layers size={30} style={{ color: 'var(--color-text-faint)', opacity: 0.4 }} />
          <p style={{ fontSize: 14, color: 'var(--color-text-muted)', margin: '12px 0 4px' }}>No cohorts yet</p>
          <p style={{ fontSize: 12, color: 'var(--color-text-faint)', margin: 0 }}>Create one to group blocks by maturity and track picking passes.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {cohorts.map(c => {
            const wm = WINDOW_META[c.harvest_window] ?? WINDOW_META.mid
            const isOpen = expanded === c.id
            return (
              <div key={c.id} style={card}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button onClick={() => setExpanded(isOpen ? null : c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: 0, display: 'flex' }}>
                    {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {c.name}
                      <span style={{ fontSize: 10.5, fontWeight: 600, padding: '1px 8px', borderRadius: 999, color: wm.color, background: `color-mix(in srgb, ${wm.color} 16%, transparent)` }}>{wm.label} window</span>
                      {c.status === 'closed' && <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--color-text-faint)' }}>· closed</span>}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>
                      {c.season ? `${c.season} · ` : ''}{c.variety_name || 'mixed varieties'} · {c.block_count} block{c.block_count === 1 ? '' : 's'} · {c.round_count} round{c.round_count === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)' }}>{c.percent_picked.toFixed(0)}% picked</div>
                    {c.harvested_kg > 0 && <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{fmtKg(c.harvested_kg)}</div>}
                  </div>
                  <button onClick={() => open(c)} style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, padding: '5px 10px', borderRadius: 7, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)', cursor: 'pointer' }}>Edit</button>
                </div>
                {/* % picked bar */}
                <div style={{ height: 5, borderRadius: 3, background: 'var(--color-bg)', overflow: 'hidden', marginTop: 10 }}>
                  <div style={{ width: `${Math.min(100, c.percent_picked)}%`, height: '100%', background: wm.color }} />
                </div>
                {isOpen && <RoundsPanel cohort={c} sys={sys} />}
                {isOpen && <GddPanel cohort={c} />}
              </div>
            )
          })}
        </div>
      )}

      {showModal && <CohortModal cohort={editing} onClose={() => setShowModal(false)} />}
    </main>
  )
}

function RoundsPanel({ cohort, sys }: { cohort: Cohort; sys: MeasurementSystem }) {
  const qc = useQueryClient()
  const { data: rounds } = useQuery({ queryKey: ['cohort-rounds', cohort.id], queryFn: () => cohortsApi.rounds(cohort.id).then(r => r.data.rounds) })
  const [adding, setAdding] = useState(false)
  const remove = useMutation({
    mutationFn: (id: string) => cohortsApi.removeRound(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cohort-rounds', cohort.id] }); qc.invalidateQueries({ queryKey: ['cohorts'] }) },
  })
  const fmtKg = (kg?: number) => kg != null ? `${kgToDisplayUnit(kg, sys).toFixed(0)} ${weightUnit(sys)}` : '—'

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--color-border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text)' }}>Picking rounds</span>
        <button onClick={() => setAdding(true)} style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Add round</button>
      </div>
      {!rounds?.length ? (
        <p style={{ fontSize: 12, color: 'var(--color-text-faint)', margin: 0 }}>No rounds yet. Add one to plan a picking pass.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rounds.map(rd => {
            const sm = ROUND_STATUS[rd.status] ?? ROUND_STATUS.planned
            const date = rd.completed_date || rd.scheduled_date
            return (
              <div key={rd.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 8, background: 'var(--color-bg)' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text)', flexShrink: 0 }}>#{rd.round_number}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, color: 'var(--color-text)' }}>
                    {rd.percent_picked > 0 ? `${rd.percent_picked.toFixed(0)}% · ` : ''}{fmtKg(rd.weight_kg)}
                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: sm.color }}>{sm.label}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{date || 'unscheduled'}{rd.notes ? ` · ${rd.notes}` : ''}</div>
                </div>
                <button onClick={() => remove.mutate(rd.id)} title="Delete round" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: 2 }}><Trash2 size={14} /></button>
              </div>
            )
          })}
        </div>
      )}
      {adding && <RoundModal cohort={cohort} nextRound={(rounds?.length ?? 0) + 1} sys={sys} onClose={() => setAdding(false)} />}
    </div>
  )
}

function RoundModal({ cohort, nextRound, sys, onClose }: { cohort: Cohort; nextRound: number; sys: MeasurementSystem; onClose: () => void }) {
  const qc = useQueryClient()
  const [f, setF] = useState({ round_number: nextRound, scheduled_date: '', completed_date: '', percent_picked: '', weight: '', status: 'planned' as PickingRound['status'], notes: '' })
  const set = (k: keyof typeof f, v: string | number) => setF(p => ({ ...p, [k]: v }))
  const save = useMutation({
    mutationFn: () => cohortsApi.addRound(cohort.id, {
      round_number: f.round_number,
      scheduled_date: f.scheduled_date || undefined,
      completed_date: f.completed_date || undefined,
      percent_picked: f.percent_picked ? parseFloat(f.percent_picked) : 0,
      weight_kg: f.weight ? inputToKg(parseFloat(f.weight), sys) : undefined,
      status: f.status, notes: f.notes || undefined,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cohort-rounds', cohort.id] }); qc.invalidateQueries({ queryKey: ['cohorts'] }); onClose() },
  })
  return (
    <Modal title={`Add picking round — ${cohort.name}`} onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><label style={lbl}>Round #</label><input style={inp} type="number" min={1} value={f.round_number} onChange={e => set('round_number', parseInt(e.target.value) || 1)} /></div>
        <div><label style={lbl}>Status</label>
          <select style={inp} value={f.status} onChange={e => set('status', e.target.value)}>
            <option value="planned">Planned</option><option value="in_progress">In progress</option><option value="done">Done</option>
          </select>
        </div>
        <div><label style={lbl}>Scheduled</label><input style={inp} type="date" value={f.scheduled_date} onChange={e => set('scheduled_date', e.target.value)} /></div>
        <div><label style={lbl}>Completed</label><input style={inp} type="date" value={f.completed_date} onChange={e => set('completed_date', e.target.value)} /></div>
        <div><label style={lbl}>% picked</label><input style={inp} type="number" min={0} max={100} value={f.percent_picked} onChange={e => set('percent_picked', e.target.value)} placeholder="0" /></div>
        <div><label style={lbl}>Weight ({weightUnit(sys)})</label><input style={inp} type="number" min={0} step={0.1} value={f.weight} onChange={e => set('weight', e.target.value)} placeholder="optional" /></div>
      </div>
      <div style={{ marginTop: 10 }}><label style={lbl}>Notes</label><input style={inp} value={f.notes} onChange={e => set('notes', e.target.value)} placeholder="optional" /></div>
      <ModalButtons onClose={onClose} onSave={() => save.mutate()} saving={save.isPending} />
    </Modal>
  )
}

function CohortModal({ cohort, onClose }: { cohort: Cohort | null; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: blocks } = useQuery({ queryKey: ['blocks'], queryFn: () => blocksApi.list().then(r => r.data.blocks) })
  const { data: varieties } = useQuery({ queryKey: ['varieties'], queryFn: () => varietiesApi.list().then(r => r.data.varieties) })
  const [f, setF] = useState({
    name: cohort?.name ?? '', season: cohort?.season ?? String(new Date().getFullYear()),
    variety_id: cohort?.variety_id ?? '', harvest_window: (cohort?.harvest_window ?? 'mid') as HarvestWindow,
    status: (cohort?.status ?? 'active') as Cohort['status'], notes: cohort?.notes ?? '',
  })
  const [blockIds, setBlockIds] = useState<string[]>(cohort?.block_ids ?? [])
  const set = (k: keyof typeof f, v: string) => setF(p => ({ ...p, [k]: v }))
  const toggleBlock = (id: string) => setBlockIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const save = useMutation({
    mutationFn: () => {
      const body: Partial<Cohort> = {
        name: f.name.trim(), season: f.season.trim(), variety_id: f.variety_id || undefined,
        harvest_window: f.harvest_window, status: f.status, notes: f.notes || undefined, block_ids: blockIds,
      }
      return cohort ? cohortsApi.update(cohort.id, body) : cohortsApi.create(body)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cohorts'] }); onClose() },
  })
  const del = useMutation({
    mutationFn: () => cohortsApi.remove(cohort!.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cohorts'] }); onClose() },
  })

  return (
    <Modal title={cohort ? 'Edit cohort' : 'New cohort'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
          <div><label style={lbl}>Name *</label><input style={inp} value={f.name} onChange={e => set('name', e.target.value)} placeholder="Early Kent" autoFocus /></div>
          <div><label style={lbl}>Season</label><input style={inp} value={f.season} onChange={e => set('season', e.target.value)} placeholder="2026" /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <div><label style={lbl}>Variety</label>
            <select style={inp} value={f.variety_id} onChange={e => set('variety_id', e.target.value)}>
              <option value="">— mixed —</option>
              {varieties?.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div><label style={lbl}>Window</label>
            <select style={inp} value={f.harvest_window} onChange={e => set('harvest_window', e.target.value)}>
              <option value="early">Early</option><option value="mid">Mid</option><option value="late">Late</option>
            </select>
          </div>
          <div><label style={lbl}>Status</label>
            <select style={inp} value={f.status} onChange={e => set('status', e.target.value)}>
              <option value="active">Active</option><option value="closed">Closed</option>
            </select>
          </div>
        </div>
        <div>
          <label style={lbl}>Blocks in this cohort</label>
          <div style={{ maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, border: '1px solid var(--color-border)', borderRadius: 8, padding: 8 }}>
            {!blocks?.length ? <span style={{ fontSize: 12, color: 'var(--color-text-faint)' }}>No blocks yet — create blocks first.</span> : blocks.map(b => (
              <label key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--color-text)', cursor: 'pointer' }}>
                <input type="checkbox" checked={blockIds.includes(b.id)} onChange={() => toggleBlock(b.id)} />
                {b.name}{b.variety_name ? <span style={{ color: 'var(--color-text-muted)' }}> · {b.variety_name}</span> : null}
              </label>
            ))}
          </div>
        </div>
        <div><label style={lbl}>Notes</label><input style={inp} value={f.notes} onChange={e => set('notes', e.target.value)} placeholder="optional" /></div>
        {save.isError && <p style={{ fontSize: 12, color: '#dc2626' }}>Could not save — try again.</p>}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          {cohort ? (
            <button onClick={() => { if (confirm(`Delete cohort “${cohort.name}”? Its rounds are removed; blocks are kept.`)) del.mutate() }} disabled={del.isPending}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid color-mix(in srgb, #dc2626 45%, transparent)', background: 'none', color: '#dc2626', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
              {del.isPending ? 'Deleting…' : 'Delete'}
            </button>
          ) : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-muted)' }}>Cancel</button>
            <button onClick={() => save.mutate()} disabled={save.isPending || !f.name.trim()} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, opacity: !f.name.trim() ? 0.5 : 1 }}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

const CONF_COLOR: Record<string, string> = { high: '#16a34a', medium: '#d97706', phenology: 'var(--color-text-muted)' }

// GDD readiness + pest-timing panel (Phase 3). GDD is °F-based (US NWS convention).
function GddPanel({ cohort }: { cohort: Cohort }) {
  const qc = useQueryClient()
  const { data: g } = useQuery({ queryKey: ['cohort-gdd', cohort.id], queryFn: () => cohortsApi.gdd(cohort.id).then(r => r.data) })
  const { data: cropBases } = useQuery({ queryKey: ['crop-bases'], queryFn: () => cohortsApi.cropBases().then(r => r.data.crop_bases) })
  const { data: pestModels } = useQuery({ queryKey: ['pest-models'], queryFn: () => cohortsApi.pestModels().then(r => r.data.pest_models) })
  const { data: watches } = useQuery({ queryKey: ['cohort-watches', cohort.id], queryFn: () => cohortsApi.pestWatches(cohort.id).then(r => r.data.watches) })

  const [f, setF] = useState({ crop: '', biofix_date: '', base_temp_f: '50', target_gdd: '', gdd_offset: '0' })
  const [seeded, setSeeded] = useState(false)
  // Seed the form once from the saved config.
  if (g && !seeded) {
    setSeeded(true)
    setF({ crop: g.crop || '', biofix_date: g.biofix_date || '', base_temp_f: String(g.base_temp_f || 50), target_gdd: g.target_gdd != null ? String(g.target_gdd) : '', gdd_offset: String(g.gdd_offset || 0) })
  }
  const set = (k: keyof typeof f, v: string) => setF(p => ({ ...p, [k]: v }))
  const pickCrop = (crop: string) => {
    const base = cropBases?.find(c => c.crop === crop)?.base_temp_f
    setF(p => ({ ...p, crop, base_temp_f: base != null ? String(base) : p.base_temp_f }))
  }
  const saveGdd = useMutation({
    mutationFn: () => cohortsApi.setGdd(cohort.id, {
      crop: f.crop, biofix_date: f.biofix_date, base_temp_f: parseFloat(f.base_temp_f) || 50,
      target_gdd: f.target_gdd ? parseFloat(f.target_gdd) : null, gdd_offset: parseFloat(f.gdd_offset) || 0,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cohort-gdd', cohort.id] }),
  })

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--color-border)' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text)', marginBottom: 8 }}>GDD readiness <span style={{ fontWeight: 400, color: 'var(--color-text-faint)' }}>(°F-based)</span></div>

      {/* Config */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 8 }}>
        <div><label style={lbl}>Crop</label>
          <select style={inp} value={f.crop} onChange={e => pickCrop(e.target.value)}>
            <option value="">—</option>
            {cropBases?.map(c => <option key={c.crop} value={c.crop}>{c.crop}</option>)}
          </select>
        </div>
        <div><label style={lbl}>Biofix date</label><input style={inp} type="date" value={f.biofix_date} onChange={e => set('biofix_date', e.target.value)} /></div>
        <div><label style={lbl}>Base °F</label><input style={inp} type="number" value={f.base_temp_f} onChange={e => set('base_temp_f', e.target.value)} /></div>
        <div><label style={lbl}>Target GDD</label><input style={inp} type="number" value={f.target_gdd} onChange={e => set('target_gdd', e.target.value)} placeholder="optional" /></div>
        <div><label style={lbl}>Start GDD</label><input style={inp} type="number" value={f.gdd_offset} onChange={e => set('gdd_offset', e.target.value)} title="GDD already accrued at biofix (seed from an external source)" /></div>
      </div>
      <button onClick={() => saveGdd.mutate()} disabled={saveGdd.isPending} style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 10 }}>
        {saveGdd.isPending ? 'Saving…' : 'Save GDD settings'}
      </button>

      {/* Readiness readout */}
      {g?.configured && g.biofix_date && (
        <div style={{ padding: '8px 10px', borderRadius: 8, background: 'var(--color-bg)', marginBottom: 10, fontSize: 12, color: 'var(--color-text)' }}>
          <strong>{g.accumulated_gdd.toFixed(0)} GDD</strong> accumulated since {g.biofix_date}
          {g.daily_rate > 0 && <span style={{ color: 'var(--color-text-muted)' }}> · ~{g.daily_rate.toFixed(1)}/day</span>}
          {g.predicted_ready && <span> · <strong style={{ color: '#16a34a' }}>ready ~{g.predicted_ready}</strong>{g.days_to_target != null ? ` (${g.days_to_target}d)` : ''}</span>}
          {g.target_gdd != null && g.days_to_target === 0 && <span style={{ color: '#16a34a' }}> · target reached ✓</span>}
          {!g.has_weather && <div style={{ fontSize: 11, color: 'var(--color-text-faint)', marginTop: 2 }}>No on-farm weather history for this range — connect a weather station for live accumulation, or enter a Start GDD.</div>}
        </div>
      )}

      {/* Pest watches */}
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text)', margin: '6px 0 6px' }}>Pest watch</div>
      {watches && watches.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
          {watches.map(wv => <PestWatchRow key={wv.id} w={wv} cohortId={cohort.id} />)}
        </div>
      )}
      <AddPestWatch cohortId={cohort.id} models={pestModels ?? []} active={watches ?? []} />
    </div>
  )
}

function PestWatchRow({ w, cohortId }: { w: PestWatch; cohortId: string }) {
  const qc = useQueryClient()
  const remove = useMutation({ mutationFn: () => cohortsApi.removePestWatch(w.id), onSuccess: () => qc.invalidateQueries({ queryKey: ['cohort-watches', cohortId] }) })
  const cc = CONF_COLOR[w.confidence] ?? 'var(--color-text-muted)'
  return (
    <div style={{ padding: '7px 10px', borderRadius: 8, background: 'var(--color-bg)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-text)', flex: 1 }}>{w.pest_name}
          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: cc }}>{w.confidence}</span>
        </span>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>biofix {w.biofix_date}</span>
        <button onClick={() => remove.mutate()} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: 2 }}><X size={13} /></button>
      </div>
      {w.stages.length > 0 ? (
        <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)', marginTop: 2 }}>
          {w.accumulated_gdd.toFixed(0)} GDD ·
          {w.current_stage ? <span style={{ color: 'var(--color-text)' }}> now: {w.current_stage.name}</span> : ' not started'}
          {w.next_stage && <span> → next: <strong style={{ color: '#d97706' }}>{w.next_stage.name}</strong> at {w.next_stage.gdd} GDD{w.next_stage.action ? ` (${w.next_stage.action})` : ''}</span>}
          {!w.has_weather && <span style={{ color: 'var(--color-text-faint)' }}> · no weather data</span>}
        </div>
      ) : (
        <div style={{ fontSize: 11.5, color: 'var(--color-text-faint)', marginTop: 2 }}>Phenology-based — scout per the model's guidance (no GDD thresholds).</div>
      )}
    </div>
  )
}

function AddPestWatch({ cohortId, models, active }: { cohortId: string; models: PestModel[]; active: PestWatch[] }) {
  const qc = useQueryClient()
  const [modelId, setModelId] = useState('')
  const [biofix, setBiofix] = useState('')
  const add = useMutation({
    mutationFn: () => cohortsApi.addPestWatch(cohortId, { pest_model_id: modelId, biofix_date: biofix }),
    onSuccess: () => { setModelId(''); setBiofix(''); qc.invalidateQueries({ queryKey: ['cohort-watches', cohortId] }) },
  })
  const activeIds = new Set(active.map(a => a.pest_model_id))
  const available = models.filter(m => !activeIds.has(m.id))
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ flex: 2, minWidth: 140 }}>
        <label style={lbl}>Add a pest model</label>
        <select style={inp} value={modelId} onChange={e => setModelId(e.target.value)}>
          <option value="">— select pest —</option>
          {available.map(m => <option key={m.id} value={m.id}>{m.pest_name} ({m.crop}{m.global ? '' : ' · custom'})</option>)}
        </select>
      </div>
      <div style={{ flex: 1, minWidth: 120 }}><label style={lbl}>Pest biofix</label><input style={inp} type="date" value={biofix} onChange={e => setBiofix(e.target.value)} /></div>
      <button onClick={() => add.mutate()} disabled={add.isPending || !modelId || !biofix} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', cursor: add.isPending || !modelId || !biofix ? 'not-allowed' : 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 12.5, fontWeight: 600, opacity: !modelId || !biofix ? 0.5 : 1 }}>
        {add.isPending ? 'Adding…' : 'Watch'}
      </button>
    </div>
  )
}

// Shared modal shell.
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card, width: 520, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text)' }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  )
}
function ModalButtons({ onClose, onSave, saving }: { onClose: () => void; onSave: () => void; saving: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
      <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-muted)' }}>Cancel</button>
      <button onClick={onSave} disabled={saving} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600 }}>{saving ? 'Saving…' : 'Save'}</button>
    </div>
  )
}

export default CohortsPage
